// Solomon Portal — typed fetch wrapper for `post-reply` Edge Function
// (issue #16 frontend half, Day 4).
//
// Contract:
//   POST /functions/v1/post-reply
//     headers: Authorization: Bearer <jwt>, Idempotency-Key: <uuid>
//     body: { conversationNumber: int, text: string, attachmentIds?: string[] }
//
//   200 OK -> { threadId, conversationId, conversationNumber }
//   400 -> { error: 'rejected', reason: <typed> }
//   401 -> unauthorized
//   403 -> { error: 'not_available_in_portal' } (deactivated OR not on convo)
//   500 -> upstream / config error
//   502 -> { error: 'upstream_unavailable' } (HS upstream non-2xx)
//
// Spec: SPEC-reply-composer.md §2.1 + §8.

import { supabase } from './supabase';
import { env } from './env';
import { logger } from './logger';

export interface PostReplyResponse {
  threadId: number | null;
  conversationId: number;
  conversationNumber: number;
}

/** Reason codes mirrored from the Edge Function's typed rejection. */
export type PostReplyRejectionReason =
  | 'invalid_json'
  | 'invalid_conversation_number'
  | 'empty'
  | 'too_long'
  | 'too_many_attachments'
  | 'invalid_attachment_id_shape'
  | 'attachment_resolution_failed'
  | 'missing_or_invalid_idempotency_key';

export type PostReplyResult =
  | { kind: 'ok'; response: PostReplyResponse }
  | { kind: 'rejected'; reason: PostReplyRejectionReason }
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden' }
  | { kind: 'upstream_unavailable' }
  | { kind: 'config_error' }
  | { kind: 'network_error' }
  | { kind: 'aborted' };

export interface PostReplyOpts {
  conversationNumber: number;
  text: string;
  attachmentIds?: string[];
  /** REQUIRED. Mint a fresh UUID per Send button press. */
  idempotencyKey: string;
  signal?: AbortSignal;
}

/** Send a reply on an existing conversation. Never throws. */
export async function postReply(opts: PostReplyOpts): Promise<PostReplyResult> {
  let session;
  try {
    const { data } = await supabase.auth.getSession();
    session = data.session;
  } catch (err: unknown) {
    logger.error({
      source: 'post-reply-api.getSession',
      message: 'getSession threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }
  if (!session) return { kind: 'unauthenticated' };

  const body = {
    conversationNumber: opts.conversationNumber,
    text: opts.text,
    ...(opts.attachmentIds && opts.attachmentIds.length > 0
      ? { attachmentIds: opts.attachmentIds }
      : {}),
  };

  let res: Response;
  try {
    res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/post-reply`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'Idempotency-Key': opts.idempotencyKey,
      },
      body: JSON.stringify(body),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  } catch (err: unknown) {
    if (opts.signal?.aborted) return { kind: 'aborted' };
    logger.error({
      source: 'post-reply-api.fetch',
      message: 'fetch threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }

  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status === 502) return { kind: 'upstream_unavailable' };

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    return { kind: 'config_error' };
  }

  if (res.status === 400) {
    const reason = (parsed as { reason?: string })?.reason;
    if (
      reason === 'invalid_json' ||
      reason === 'invalid_conversation_number' ||
      reason === 'empty' ||
      reason === 'too_long' ||
      reason === 'too_many_attachments' ||
      reason === 'invalid_attachment_id_shape' ||
      reason === 'attachment_resolution_failed' ||
      reason === 'missing_or_invalid_idempotency_key'
    ) {
      return { kind: 'rejected', reason };
    }
    return { kind: 'config_error' };
  }

  if (res.status !== 200) return { kind: 'config_error' };

  const obj = parsed as Partial<PostReplyResponse>;
  if (
    typeof obj.conversationId !== 'number' ||
    typeof obj.conversationNumber !== 'number' ||
    (obj.threadId !== null && typeof obj.threadId !== 'number')
  ) {
    logger.error({
      source: 'post-reply-api.fetch',
      message: 'response shape invalid',
      payload: { keys: Object.keys(obj) },
    });
    return { kind: 'config_error' };
  }

  return {
    kind: 'ok',
    response: {
      threadId: obj.threadId ?? null,
      conversationId: obj.conversationId,
      conversationNumber: obj.conversationNumber,
    },
  };
}

/** Mint a fresh idempotency key. Use one per Send button press. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
